// VOYA9 consumer realtime — single shared WebSocket connection for the
// authenticated member surfaces (dashboard, care-team chat, messages,
// any consultation timeline, etc.).
//
// Design:
//   • One physical WS per tab. Multiple `useConsumerRealtime()` hooks
//     across components share the same socket and a tiny in-memory
//     pub/sub for typed events.
//   • Auth via ?token= query param at connect time (existing consumer
//     JWT). Token rotation triggers a reconnect.
//   • 35s ping cadence with `"ping"` text frame; server replies with
//     `{type:"pong"}`.
//   • Exponential backoff reconnect (3s → cap 30s) with full jitter so
//     a fleet doesn't thunder a server restart.
//   • Listeners receive parsed event payloads. Handlers should filter
//     by id (consultationId / message.id / event.id) before patching
//     state — the server may broadcast unrelated events.
//
// Public API:
//   useConsumerRealtime(callback)
//     callback: (event: any) => void
//     Subscribes to ALL incoming events for the lifetime of the
//     component. Use it inside an effect with handler returned from
//     useEvent(...) for ergonomic `event.type === "..."` filtering.
//
//   useEvent(type, callback)
//     Filtered subscription — only fires for events whose `type`
//     matches. `type` may be a string or an array of strings.
import { useEffect, useMemo, useRef } from "react";
import { getToken } from "@/lib/api";

const PING_INTERVAL_MS = 35_000;
const RECONNECT_BASE_MS = 3_000;
const RECONNECT_CAP_MS = 30_000;
// After this many consecutive failed connects we stop trying — the
// realtime channel is non-essential (REST polling still works) so a
// truly broken WS path on production should NOT spam the console
// forever. The next page navigation resets the counter via teardown.
const RECONNECT_MAX_ATTEMPTS = 6;

// ---- module-level singleton ----
let socket = null;
let socketState = "idle"; // "idle" | "connecting" | "open" | "closing" | "closed"
let reconnectAttempts = 0;
let reconnectTimer = null;
let pingTimer = null;
let lastToken = null;
const listeners = new Set(); // Set<(event) => void>

function wsUrlFor(token) {
  if (!token) return null;
  // Use the same base as REST — on production REACT_APP_BACKEND_URL is
  // the FE origin (voya9.com) while REACT_APP_API_BASE is the BE
  // (healthyhorizons.clinic). The WS lives with the BE.
  const base = process.env.REACT_APP_API_BASE || process.env.REACT_APP_BACKEND_URL || "";
  if (!base) return null;
  // Convert https://host → wss://host (and http → ws for local dev).
  const wsBase = base.replace(/^http:/, "ws:").replace(/^https:/, "wss:");
  return `${wsBase}/api/ws/consumer?token=${encodeURIComponent(token)}`;
}

function clearTimers() {
  if (pingTimer) { clearInterval(pingTimer); pingTimer = null; }
  if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
}

function connect() {
  const token = getToken();
  if (!token) {
    // No token → tear down any existing socket and stop trying. We'll
    // pick up again on the next call once auth lands.
    teardown();
    return;
  }
  // If we already have a viable connection, do nothing. Token rotation
  // is handled at the "ensure" layer.
  if (socket && (socketState === "open" || socketState === "connecting")) return;

  lastToken = token;
  socketState = "connecting";
  const url = wsUrlFor(token);
  if (!url) return;

  let ws;
  try {
    ws = new WebSocket(url);
  } catch {
    socketState = "closed";
    scheduleReconnect();
    return;
  }
  socket = ws;

  ws.onopen = () => {
    socketState = "open";
    reconnectAttempts = 0;
    // Start heartbeat
    if (pingTimer) clearInterval(pingTimer);
    pingTimer = setInterval(() => {
      try {
        if (socket && socket.readyState === WebSocket.OPEN) socket.send("ping");
      } catch { /* ignore — onclose will handle */ }
    }, PING_INTERVAL_MS);
  };

  ws.onmessage = (msgEvt) => {
    let payload;
    try { payload = JSON.parse(msgEvt.data); } catch { return; }
    if (!payload || typeof payload !== "object") return;
    // Pong is internal — don't notify listeners.
    if (payload.type === "pong") return;
    for (const fn of Array.from(listeners)) {
      try { fn(payload); } catch { /* swallow listener errors */ }
    }
  };

  ws.onclose = () => {
    socketState = "closed";
    socket = null;
    if (pingTimer) { clearInterval(pingTimer); pingTimer = null; }
    if (listeners.size > 0 && getToken()) scheduleReconnect();
  };

  ws.onerror = () => {
    // Error is followed by close — we handle reconnect there. Just
    // make sure the socket is clean if it never opened.
    if (socketState === "connecting") {
      try { ws.close(); } catch { /* ignore */ }
    }
  };
}

function scheduleReconnect() {
  if (reconnectTimer) return;
  if (reconnectAttempts >= RECONNECT_MAX_ATTEMPTS) {
    // Give up quietly. The page still works without realtime — we just
    // don't get push updates until the next teardown/connect cycle
    // (e.g. visibility change, sign-out + sign-in).
    return;
  }
  reconnectAttempts += 1;
  const exp = Math.min(RECONNECT_CAP_MS, RECONNECT_BASE_MS * 2 ** Math.min(4, reconnectAttempts - 1));
  const jitter = exp * (0.5 + Math.random() * 0.5);
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    connect();
  }, jitter);
}

function teardown() {
  clearTimers();
  if (socket) {
    socketState = "closing";
    try { socket.close(); } catch { /* ignore */ }
  }
  socket = null;
  socketState = "idle";
  reconnectAttempts = 0;
}

function ensureConnection() {
  const token = getToken();
  if (!token) {
    teardown();
    return;
  }
  // Token rotated since last connect — drop and reconnect with the
  // fresh token.
  if (token !== lastToken && socket) {
    teardown();
  }
  connect();
}

// ---- public hooks ----

// Subscribe to ALL events. Returned callback is invoked once per event.
// Holds the WS open while at least one listener is mounted.
export function useConsumerRealtime(callback) {
  const cbRef = useRef(callback);
  useEffect(() => { cbRef.current = callback; }, [callback]);

  useEffect(() => {
    const wrapped = (evt) => {
      try { cbRef.current?.(evt); } catch { /* swallow */ }
    };
    listeners.add(wrapped);
    ensureConnection();
    return () => {
      listeners.delete(wrapped);
      // If nothing else is listening AND we have a socket, leave it
      // open for a short window so navigations don't churn the
      // connection. The next mount within ~5s reuses it; otherwise we
      // tear down.
      if (listeners.size === 0) {
        setTimeout(() => {
          if (listeners.size === 0) teardown();
        }, 5_000);
      }
    };
  }, []);
}

// Filtered subscription. `type` can be a single event-type string or
// an array of strings.
export function useEvent(type, callback) {
  const types = useMemo(() => (Array.isArray(type) ? type : [type]), [type]);
  useConsumerRealtime((evt) => {
    if (!evt || !evt.type) return;
    if (types.includes(evt.type)) callback(evt);
  });
}

// Re-syncs the WS on tab visibility changes — paired with one-shot
// fetches at the call site so a backgrounded tab catches up after the
// device wakes.
export function useResyncOnVisible(refetch) {
  const refRefetch = useRef(refetch);
  useEffect(() => { refRefetch.current = refetch; }, [refetch]);
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState !== "visible") return;
      // Coming back to the tab — give realtime a fresh retry budget so
      // a longish backgrounded period doesn't leave us stuck on the
      // "we gave up" state.
      resetReconnectBudget();
      ensureConnection();
      try { refRefetch.current?.(); } catch { /* ignore */ }
    };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onVis);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onVis);
    };
  }, []);
}
